'use client';

import * as SliderPrimitive from '@radix-ui/react-slider';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { Label } from '../../stories/primitives/visual-elements';

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  isShowTooltip = true,
  tooltipPosition = 'top',
  tooltipNode,
  size = 'md',
  showThumbOnHover = false,
  ...props
}: React.ComponentProps<typeof SliderPrimitive.Root> & {
  /** 툴팁 표시 여부 */
  isShowTooltip?: boolean;
  /** 툴팁 위치 */
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * 툴팁 내용
   * - string: Label 컴포넌트 내부에 텍스트로 렌더링
   * - ReactElement: Label 없이 컴포넌트 그대로 렌더링
   * - 함수: (value, index) => ReactNode 형태로 동적 렌더링
   * - 미지정: 현재 값(value)을 Label로 표시
   */
  tooltipNode?:
    | string
    | React.ReactElement
    | ((value: number, index: number) => React.ReactNode);
  /** 크기 */
  size?: 'sm' | 'md';
  /** hover 시에만 Thumb 표시 여부 */
  showThumbOnHover?: boolean;
}) {
  const _values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [value, defaultValue, min, max],
  );

  const tooltipClasses = React.useMemo(() => {
    switch (tooltipPosition) {
      case 'top':
        return 'absolute -top-md left-1/2 -translate-x-1/2';
      case 'bottom':
        return 'absolute -bottom-md left-1/2 -translate-x-1/2';
      case 'left':
        return 'absolute top-1/2 -left-[36px] -translate-y-1/2';
      case 'right':
        return 'absolute top-1/2 -right-[36px] -translate-y-1/2';
      default:
        return 'absolute -top-md left-1/2 -translate-x-1/2';
    }
  }, [tooltipPosition]);

  // 툴팁 내용 렌더링 헬퍼 함수
  const renderTooltipContent = React.useCallback(
    (currentValue: number, index: number) => {
      // 1. 함수인 경우 - 콜백 호출
      if (typeof tooltipNode === 'function') {
        return tooltipNode(currentValue, index);
      }

      // 2. string인 경우 - Label 안에 렌더링
      if (typeof tooltipNode === 'string') {
        return (
          <Label
            color="neutral"
            size="small"
            isHighlight={true}
          >
            {tooltipNode}
          </Label>
        );
      }

      // 3. ReactElement인 경우 - Label 없이 그대로 렌더링
      if (React.isValidElement(tooltipNode)) {
        return tooltipNode;
      }

      // 4. 미지정(undefined)인 경우 - 기본값(value) Label로 렌더링
      return (
        <Label
          color="neutral"
          size="small"
          isHighlight={true}
        >
          {currentValue}
        </Label>
      );
    },
    [tooltipNode],
  );

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={cn(
        'group/slider relative flex w-full touch-none items-center transition-all select-none',
        'data-[disabled]:opacity-50',
        'data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          'relative grow cursor-pointer overflow-hidden rounded-full bg-comp-slider-track-bg',
          `${size === 'md' && 'data-[orientation=horizontal]:h-1.5'} ${size === 'sm' && 'data-[orientation=horizontal]:h-1'} data-[orientation=horizontal]:w-full`,
          'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5',
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn(
            'absolute',
            'bg-comp-slider-range-bg data-[disabled]:bg-comp-slider-range-disabled',
            'data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: _values.length }, (_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          key={index}
          className={cn(
            'group relative block size-[20px] shrink-0 cursor-pointer rounded-full border-none focus-visible:outline-hidden',
            'bg-comp-slider-thumb-bg inset-shadow-thick-sm',
            'data-[disabled]:pointer-events-none data-[disabled]:bg-comp-slider-thumb-disabled',
            showThumbOnHover && [
              'transition-[color,box-shadow,opacity]',
              'opacity-0 group-hover/slider:opacity-100',
            ],
          )}
        >
          {isShowTooltip && (
            <div
              className={cn(
                tooltipClasses,
                'opacity-0 transition-opacity duration-150 ease-in-out group-hover:opacity-100',
              )}
            >
              {renderTooltipContent(_values[index] ?? 0, index)}
            </div>
          )}
        </SliderPrimitive.Thumb>
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider as RadixSlider };
